/**
 * MemorialCardHero — renders a departed member's photo, name, dates and
 * quote on top of a chosen memorial-card template artwork. Scales to the
 * container width; positions come from the backend template layout spec.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { genderSilhouette } from "@/lib/silhouette";const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function fmtLifeDate(d, yearOnly) {
  if (!d) return "";
  if (yearOnly || /^\d{4}$/.test(d)) return d.slice(0, 4);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return d;
  return `${parseInt(m[3], 10)} ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

export function lifespanLine(person) {
  const b = fmtLifeDate(person.birth_date, person.birth_date_is_year_only);
  const dd = fmtLifeDate(person.death_date, person.death_date_is_year_only);
  if (b && dd) return `${b} — ${dd}`;
  return dd ? `— ${dd}` : b;
}

function resizeToDataUrl(file, max = 1000) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  });
}

function FramedPhoto({ layout, photoUrl, person, w, onAddPhoto }) {
  const [broken, setBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!layout) return null;
  const style = {
    position: "absolute",
    left: `${layout.left}%`,
    top: `${layout.top}%`,
    width: `${layout.width}%`,
    height: `${layout.height}%`,
    borderRadius: layout.shape === "ellipse" ? "50%" : "3px",
    overflow: "hidden",
  };
  if (photoUrl && !broken) {
    return (
      <div style={style}>
        <img src={photoUrl} alt={person.name} crossOrigin="anonymous" onError={() => setBroken(true)}
          className="w-full h-full object-cover" data-testid="memorial-card-photo" />
      </div>
    );
  }
  const silhouette = genderSilhouette(person.gender);
  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onAddPhoto) return;
    setBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      await onAddPhoto(dataUrl);
      setBroken(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ ...style }} data-testid="memorial-card-silhouette">
      <img src={silhouette} alt="Portrait placeholder" className="w-full h-full object-cover" />
      <div style={{ position: "absolute", left: "8%", right: "8%", bottom: "9%",
        display: "flex", justifyContent: "center" }}>
        {onAddPhoto ? (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile}
              data-testid="memorial-card-photo-input" />
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
              data-testid="memorial-card-photo-caption"
              style={{ background: "rgba(255,255,255,0.92)", color: "#5F5240", cursor: "pointer",
                border: "1px dashed #8A7A63", borderRadius: 999,
                padding: `${Math.max(2, w * 0.006)}px ${Math.max(6, w * 0.02)}px`,
                fontSize: Math.max(9, w * 0.024), fontStyle: "italic", fontWeight: 600,
                boxShadow: "0 1px 4px rgba(0,0,0,.12)", whiteSpace: "nowrap" }}>
              {busy ? "Uploading…" : "Insert the photo here"}
            </button>
          </>
        ) : (
          <span data-testid="memorial-card-photo-caption"
            style={{ background: "rgba(255,255,255,0.88)", color: "#5F5240",
              borderRadius: 999, padding: `${Math.max(2, w * 0.006)}px ${Math.max(6, w * 0.02)}px`,
              fontSize: Math.max(9, w * 0.024), fontStyle: "italic", fontWeight: 600,
              boxShadow: "0 1px 4px rgba(0,0,0,.12)", whiteSpace: "nowrap" }}>
            Insert the photo here
          </span>
        )}
      </div>
    </div>
  );
}

function Zone({ top, h, left = 8, width = 84, children, style }) {
  return (
    <div style={{ position: "absolute", top: `${top}%`, height: `${h}%`, left: `${left}%`,
      width: `${width}%`, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", textAlign: "center", ...style }}>
      {children}
    </div>
  );
}

export default function MemorialCardHero({ template, person, photoUrl, quote, innerRef, onAddPhoto }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(360);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.offsetWidth || 360));
    ro.observe(el);
    setW(el.offsetWidth || 360);
    return () => ro.disconnect();
  }, []);

  const [aw, ah] = template.aspect || [1122, 1402];
  const q = (quote || "").trim() || "Forever in our hearts";
  const dates = lifespanLine(person);
  const places = [person.birth_place, person.death_place].filter(Boolean).join(" · ");
  const serif = "'Playfair Display', Georgia, serif";

  const setRefs = (el) => {
    wrapRef.current = el;
    if (innerRef) innerRef.current = el;
  };

  if (!template.image_url) {
    // Classic — CSS-only saffron/parchment keepsake.
    return (
      <div ref={setRefs} data-testid="memorial-card-hero"
        style={{ aspectRatio: `${aw} / ${ah}`, position: "relative", width: "100%",
          background: "linear-gradient(160deg, #FFF7ED 0%, #FDEBD2 55%, #FAE3C0 100%)",
          border: `${Math.max(3, w * 0.012)}px double #C2410C`, borderRadius: 10,
          boxShadow: "0 12px 40px rgba(120,53,15,.18)", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: w * 0.03,
          border: "1px solid rgba(194,65,12,.35)", borderRadius: 6, pointerEvents: "none" }} />
        <Zone top={5} h={4}>
          <span style={{ fontSize: w * 0.026, letterSpacing: "0.35em", color: "#B45309",
            fontWeight: 600, textTransform: "uppercase" }}>In Loving Memory</span>
        </Zone>
        <FramedPhoto
          layout={{ left: 31, top: 11, width: 38, height: 30.5, shape: "ellipse" }}
          photoUrl={photoUrl} person={person} w={w} onAddPhoto={onAddPhoto} />
        <div style={{ position: "absolute", left: "30%", top: "10.4%", width: "40%", height: "31.7%",
          borderRadius: "50%", border: "2px solid #C2410C", pointerEvents: "none", opacity: 0.6 }} />
        <Zone top={45} h={9}>
          <span style={{ fontFamily: serif, fontSize: w * 0.062, color: "#3D2B1F", lineHeight: 1.1 }}>{person.name}</span>
          {person.nickname && <span style={{ fontSize: w * 0.028, color: "#8a6d3b", fontStyle: "italic" }}>“{person.nickname}”</span>}
        </Zone>
        <Zone top={56} h={8}>
          <span style={{ fontFamily: serif, fontSize: w * 0.036, color: "#7c4a12" }}>{dates}</span>
          {places && <span style={{ fontSize: w * 0.024, color: "#8a6d3b", marginTop: w * 0.008 }}>{places}</span>}
        </Zone>
        <Zone top={68} h={5}>
          <span style={{ fontSize: w * 0.05, color: "#C2410C" }}>❁ ❁ ❁</span>
        </Zone>
        <Zone top={76} h={14} left={14} width={72}>
          <span style={{ fontFamily: serif, fontStyle: "italic", fontSize: w * 0.032,
            color: "#5b4a2f", lineHeight: 1.45 }}>“{q}”</span>
        </Zone>
        <Zone top={92} h={4}>
          <span style={{ fontSize: w * 0.02, letterSpacing: "0.2em", color: "#B45309", textTransform: "uppercase" }}>
            IndianFamilyTree.com
          </span>
        </Zone>
      </div>
    );
  }

  const z = template.zones || {};
  return (
    <div ref={setRefs} data-testid="memorial-card-hero"
      style={{ aspectRatio: `${aw} / ${ah}`, position: "relative", width: "100%",
        borderRadius: 10, overflow: "hidden", boxShadow: "0 12px 40px rgba(15,23,42,.25)" }}>
      <img src={template.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <FramedPhoto layout={template.photo} photoUrl={photoUrl} person={person} w={w} onAddPhoto={onAddPhoto} />
      <Zone top={z.name_top} h={z.name_h}>
        <span style={{ fontFamily: serif, fontSize: w * 0.056, color: template.text_color,
          lineHeight: 1.08, textShadow: "0 1px 3px rgba(0,0,0,.12)" }}>{person.name}</span>
        {person.nickname && (
          <span style={{ fontSize: w * 0.026, color: template.muted_color, fontStyle: "italic" }}>“{person.nickname}”</span>
        )}
      </Zone>
      <Zone top={z.dates_top} h={z.dates_h}>
        <span style={{ fontFamily: serif, fontSize: w * 0.034, color: template.text_color }}>{dates}</span>
        {places && (
          <span style={{ fontSize: w * 0.023, color: template.muted_color, marginTop: w * 0.006 }}>{places}</span>
        )}
      </Zone>
      <Zone top={z.quote_top} h={z.quote_h} left={z.quote_left} width={z.quote_w}>
        <span style={{ fontFamily: serif, fontStyle: "italic", fontSize: w * 0.029,
          color: template.text_color, lineHeight: 1.4 }}>“{q}”</span>
      </Zone>
    </div>
  );
}
